import { useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { adminApi, currency, categoryMeta } from "@/lib/api";
import { Plus, Pencil, Trash2, Star } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const empty = { name: "", description: "", category: "tables", price_per_day: 0, stock: 0, image_urls: [], image_captions: [], featured: false, active: true };

export default function AdminProducts() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);

  const { data: products = [] } = useQuery({ queryKey: ["admin-products"], queryFn: async () => (await adminApi.get("/products")).data });
  const { data: categories = [] } = useQuery({ queryKey: ["categories"], queryFn: async () => (await adminApi.get("/categories")).data });

  const openNew = () => { setEditing(null); setForm(empty); setImages([]); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p); setForm({ ...p });
    setImages((p.image_urls || []).map((u, i) => ({ url: u, caption: (p.image_captions || [])[i] || "" })));
    setOpen(true);
  };

  const save = async () => {
    if (!form.name || !form.category) { toast.error("Name and category are required"); return; }
    const clean = images.filter((im) => im.url.trim());
    const payload = {
      ...form,
      price_per_day: Number(form.price_per_day),
      stock: Number(form.stock),
      image_urls: clean.map((im) => im.url.trim()),
      image_captions: clean.map((im) => im.caption || ""),
    };
    delete payload.id; delete payload.created_at; delete payload._id;
    try {
      if (editing) await adminApi.put(`/admin/products/${editing.id}`, payload);
      else await adminApi.post("/admin/products", payload);
      toast.success(editing ? "Product updated" : "Product created");
      setOpen(false);
      qc.invalidateQueries({ queryKey: ["admin-products"] });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (id) => {
    try {
      await adminApi.delete(`/admin/products/${id}`);
      toast.success("Product deleted");
      qc.invalidateQueries({ queryKey: ["admin-products"] });
    } catch { toast.error("Delete failed"); }
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = await Promise.all(files.map(async (f) => {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await adminApi.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        return { url: data.url, caption: "" };
      }));
      setImages((prev) => [...prev, ...uploaded]);
      toast.success(`Uploaded ${uploaded.length} photo(s)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const dragIndex = useRef(null);
  const reorderImages = (from, to) => {
    if (from == null || to == null || from === to) return;
    setImages((prev) => {
      if (from < 0 || to < 0 || from >= prev.length || to >= prev.length) return prev;
      const arr = [...prev];
      const [moved] = arr.splice(from, 1);
      arr.splice(to, 0, moved);
      return arr;
    });
  };
  const makeCover = (i) => reorderImages(i, 0);
  const removeImage = (i) => setImages((prev) => prev.filter((_, idx) => idx !== i));
  const updateCaption = (i) => (e) => {
    const val = e.target.value;
    setImages((prev) => prev.map((im, idx) => (idx === i ? { ...im, caption: val } : im)));
  };
  const addUrl = (url) => {
    const u = (url || "").trim();
    if (u) setImages((prev) => [...prev, { url: u, caption: "" }]);
  };

  const setF = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="overline-label mb-1">Inventory</p>
          <h1 className="font-serif text-3xl text-cream">Products <span className="text-muted2 text-lg">({products.length})</span></h1>
        </div>
        <button data-testid="admin-add-product-button" onClick={openNew} className="btn-gold"><Plus className="w-4 h-4" /> Add Product</button>
      </div>

      <div data-testid="admin-products-table" className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {products.map((p) => (
          <div key={p.id} data-testid={`admin-product-${p.id}`} className="gold-card !border-gold/20 p-4 flex gap-4">
            <img src={p.image_urls?.[0]} alt="" className="w-20 h-20 rounded-lg object-cover shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-serif text-cream truncate">{p.name}</h3>
                {p.featured && <Star className="w-4 h-4 text-gold shrink-0 fill-gold" />}
              </div>
              <p className="text-xs text-muted2">{categoryMeta[p.category]?.label || p.category}</p>
              <p className="text-gold text-sm mt-1">{currency(p.price_per_day)}/day · {p.stock} stock</p>
              {!p.active && <span className="text-[10px] text-red-300 border border-red-400/40 rounded-full px-2 py-0.5 mt-1 inline-block">Inactive</span>}
              <div className="flex gap-2 mt-3">
                <button data-testid={`admin-edit-product-${p.id}`} onClick={() => openEdit(p)} className="text-xs text-gold hover:text-gold-bright flex items-center gap-1"><Pencil className="w-3 h-3" /> Edit</button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button data-testid={`admin-delete-product-${p.id}`} className="text-xs text-red-300 hover:text-red-400 flex items-center gap-1"><Trash2 className="w-3 h-3" /> Delete</button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-ink-800 border-gold/30 text-cream">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete {p.name}?</AlertDialogTitle>
                      <AlertDialogDescription className="text-muted2">This action cannot be undone.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="bg-ink-700 border-gold/30 text-cream">Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => remove(p.id)} className="bg-red-600 hover:bg-red-700 text-white">Delete</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-ink-800 border-gold/30 text-cream max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{editing ? "Edit Product" : "New Product"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs text-muted2 mb-1 block">Name</label>
              <input data-testid="product-form-name" value={form.name} onChange={setF("name")} className="w-full bg-ink border border-gold/25 rounded-lg px-3 py-2 text-cream focus:outline-none focus:border-gold" />
            </div>
            <div>
              <label className="text-xs text-muted2 mb-1 block">Description</label>
              <textarea data-testid="product-form-description" value={form.description} onChange={setF("description")} rows={3} className="w-full bg-ink border border-gold/25 rounded-lg px-3 py-2 text-cream focus:outline-none focus:border-gold resize-none" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted2 mb-1 block">Category</label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="product-form-category" className="bg-ink border-gold/25 text-cream"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-ink-800 border-gold/30 text-cream">
                    {categories.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-muted2 mb-1 block">Price / day ($)</label>
                <input data-testid="product-form-price" type="number" value={form.price_per_day} onChange={setF("price_per_day")} className="w-full bg-ink border border-gold/25 rounded-lg px-3 py-2 text-cream focus:outline-none focus:border-gold" />
              </div>
            </div>
            <div>
              <label className="text-xs text-muted2 mb-1 block">Stock</label>
              <input data-testid="product-form-stock" type="number" value={form.stock} onChange={setF("stock")} className="w-full bg-ink border border-gold/25 rounded-lg px-3 py-2 text-cream focus:outline-none focus:border-gold" />
            </div>
            <div>
              <label className="text-xs text-muted2 mb-1 block">Product Photos</label>
              <div className="space-y-2 mb-2">
                {images.map((img, idx) => (
                  <div key={idx} draggable
                    onDragStart={() => { dragIndex.current = idx; }}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => { reorderImages(dragIndex.current, idx); dragIndex.current = null; }}
                    data-testid={`product-image-thumb-${idx}`}
                    className="flex items-center gap-3 bg-ink border border-gold/20 rounded-lg p-2 cursor-move">
                    <div className="relative w-16 h-16 rounded-md overflow-hidden shrink-0">
                      <img src={img.url.startsWith("http") ? img.url : `${process.env.REACT_APP_BACKEND_URL}${img.url}`} alt="" className="w-full h-full object-cover pointer-events-none" />
                      {idx === 0 && <span className="absolute top-0 left-0 bg-gold text-ink text-[9px] font-semibold px-1 rounded-br">Cover</span>}
                    </div>
                    <input data-testid={`product-image-caption-${idx}`} value={img.caption} onChange={updateCaption(idx)} placeholder="Caption (optional)"
                      className="flex-1 bg-ink-700 border border-gold/20 rounded-md px-2 py-1.5 text-sm text-cream focus:outline-none focus:border-gold" />
                    <div className="flex flex-col gap-1 shrink-0">
                      {idx !== 0 && <button type="button" data-testid={`product-image-cover-${idx}`} onClick={() => makeCover(idx)} className="text-[10px] text-gold hover:text-gold-bright">Set cover</button>}
                      <button type="button" data-testid={`product-image-remove-${idx}`} onClick={() => removeImage(idx)} className="text-[10px] text-red-300 hover:text-red-400">Remove</button>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-muted2 mb-2">Drag rows to reorder · first photo is the cover · captions show in the gallery</p>
              <label className="btn-silver !py-2 cursor-pointer inline-flex">
                <input data-testid="product-form-upload" type="file" accept="image/*" multiple className="hidden" onChange={handleUpload} disabled={uploading} />
                {uploading ? "Uploading…" : "Upload Photos"}
              </label>
              <input data-testid="product-form-images" placeholder="Paste an image URL and press Enter to add"
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addUrl(e.target.value); e.target.value = ""; } }}
                className="w-full bg-ink border border-gold/25 rounded-lg px-3 py-2 text-cream focus:outline-none focus:border-gold font-mono text-xs mt-2" />
            </div>
            <div className="flex items-center gap-8">
              <label className="flex items-center gap-3 text-sm text-cream">
                <Switch data-testid="product-form-featured" checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: v })} /> Featured
              </label>
              <label className="flex items-center gap-3 text-sm text-cream">
                <Switch data-testid="product-form-active" checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /> Active
              </label>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="btn-silver">Cancel</button>
            <button data-testid="product-form-save" onClick={save} className="btn-gold">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
